import { Search as SearchIcon } from '@mui/icons-material';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import {
    Alert,
    Box,
    Button,
    Grid,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import { useState } from 'react';
import AdminPageWrapper from './AdminPageWrapper';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

// UIDs are fixed in monitoring/dashboards/*.json (backend repo) and provisioned
// identically on every machine via scripts/setup-monitoring.sh — same values
// locally and on Ubuntu.
const GRAFANA_DASHBOARDS = [
    { title: 'API Latency & Traffic', uid: 'api-latency-traffic', slug: 'api-latency-and-traffic' },
    { title: 'Pipeline Stage Performance', uid: 'pipeline-stage-performance', slug: 'pipeline-stage-performance' },
    { title: 'System Health', uid: 'system-health', slug: 'system-health' },
];

function grafanaPanelUrl({ uid, slug }) {
    return `/grafana/d/${uid}/${slug}?orgId=1&kiosk&theme=light`;
}

const InstrumentationPage = ({ darkMode, toggleDarkMode, themeVariant }) => {
    const [videoId, setVideoId] = useState('');
    const [videoStatus, setVideoStatus] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const lookupVideoStatus = async () => {
        if (!videoId.trim()) {
            return;
        }
        setLoading(true);
        setError(null);
        setVideoStatus(null);
        try {
            const response = await fetch(
                `${API_BASE_URL}/api/v3/monitoring/video-status/${encodeURIComponent(videoId.trim())}`
            );
            const text = await response.text();
            setVideoStatus(text);
        } catch (err) {
            setError(err.message || 'Failed to fetch video status');
        } finally {
            setLoading(false);
        }
    };

    return (
        <AdminPageWrapper
            title="Instrumentation"
            darkMode={darkMode}
            toggleDarkMode={toggleDarkMode}
            themeVariant={themeVariant}
        >
            <Box display="flex" alignItems="center" mb={3}>
                <MonitorHeartIcon sx={{ fontSize: 30, mr: 2, color: 'info.main' }} />
                <Typography variant="h4">Instrumentation</Typography>
            </Box>

            <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Per-video timeline
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Looks up stage-by-stage timestamps for a single video directly from the
                    backend (not from Grafana — per-video lookups don't fit a Prometheus
                    dashboard well).
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <TextField
                        size="small"
                        label="Video ID"
                        value={videoId}
                        onChange={(e) => setVideoId(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && lookupVideoStatus()}
                        sx={{ minWidth: 320 }}
                    />
                    <Button
                        variant="contained"
                        startIcon={<SearchIcon />}
                        onClick={lookupVideoStatus}
                        disabled={loading || !videoId.trim()}
                    >
                        Look up
                    </Button>
                </Box>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {videoStatus && (
                    <Box
                        component="pre"
                        sx={{
                            p: 2,
                            bgcolor: 'action.hover',
                            borderRadius: 1,
                            overflow: 'auto',
                            fontSize: '0.85rem',
                        }}
                    >
                        {videoStatus}
                    </Box>
                )}
            </Paper>

            <Grid container spacing={3}>
                {GRAFANA_DASHBOARDS.map((dashboard) => (
                    <Grid item xs={12} key={dashboard.uid}>
                        <Paper sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                {dashboard.title}
                            </Typography>
                            <Box
                                component="iframe"
                                title={dashboard.title}
                                src={grafanaPanelUrl(dashboard)}
                                sx={{ width: '100%', height: 500, border: 0 }}
                            />
                        </Paper>
                    </Grid>
                ))}
            </Grid>
        </AdminPageWrapper>
    );
};

export default InstrumentationPage;
